import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  ArrowDownUp,
  ChevronDown,
  ClipboardPaste,
  Download,
  FileDown,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';

// ImportExportMenu — single header button consolidating the four
// import/export actions that used to be separate toolbar buttons:
//
//   Import Tasks (CSV)     -> opens ImportTasksModal   (projects.task.create)
//   Paste Project Bundle   -> opens PasteProjectModal  (projects.project.edit)
//   Export Tasks (CSV)     -> downloads CSV            (projects.task.view)
//   Export Project (JSON)  -> downloads bundle         (projects.task.view)
//
// The two modals are owned by ProjectDetailPage (this menu only signals via
// callbacks); the two downloads are handled inline here. Each item is
// permission-gated independently; the whole menu hides when none apply.

function MenuItem({ icon: Icon, label, hint, onSelect, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      data-testid={testid}
      className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-boss-elevated disabled:opacity-50 transition-colors"
    >
      <Icon size={14} className="mt-0.5 shrink-0 text-boss-text-muted" />
      <span className="min-w-0">
        <span className="block text-xs font-medium text-boss-text">{label}</span>
        {hint ? (
          <span className="block text-[10px] text-boss-text-muted leading-snug">{hint}</span>
        ) : null}
      </span>
    </button>
  );
}

export default function ImportExportMenu({ project, onImportTasks, onPasteBundle }) {
  const { hasPermission } = usePermissions();
  const canImportTasks = hasPermission('projects.task.create');
  const canPaste = hasPermission('projects.project.edit');
  const canExport = hasPermission('projects.task.view');

  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!project || (!canImportTasks && !canPaste && !canExport)) return null;

  const safeName = (project.name ?? 'project').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 80) || 'project';
  const projectId = project.project_id ?? project._id;

  const download = async (url, filename, successMsg) => {
    setDownloading(true);
    try {
      const blob = await api.download(url);
      if (!blob) {
        toast.error('Could not export project.');
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      toast.success(successMsg);
    } catch {
      toast.error('Could not export project.');
    } finally {
      setDownloading(false);
      setOpen(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-1.5 text-xs shrink-0"
          data-testid="import-export-menu-trigger"
          aria-label="Import or export project data"
        >
          <ArrowDownUp size={12} />
          Import / Export
          <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
            }
          }}
          className="z-50 w-[240px] rounded-lg border border-boss-border bg-boss-surface shadow-xl animate-in fade-in-0 zoom-in-95 duration-100 py-1"
          data-testid="import-export-menu"
        >
          {/* Tasks group — CSV import/export of this project's tasks */}
          {(canImportTasks || canExport) && (
            <div data-testid="menu-group-tasks">
              <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-boss-text-muted">
                Tasks
              </p>
              {canImportTasks && (
                <MenuItem
                  icon={Upload}
                  label="Import Tasks"
                  hint="Add tasks to this project from a CSV"
                  testid="menu-import-tasks"
                  onSelect={() => {
                    setOpen(false);
                    onImportTasks?.();
                  }}
                />
              )}
              {canExport && (
                <MenuItem
                  icon={Download}
                  label="Export Tasks"
                  hint="All tasks as a CSV spreadsheet"
                  testid="menu-export-tasks"
                  disabled={downloading}
                  onSelect={() =>
                    download(
                      `${API_URL}/api/projects/projects/${projectId}/export?format=csv`,
                      `${safeName}_tasks.csv`,
                      'Export downloaded'
                    )
                  }
                />
              )}
            </div>
          )}
          {(canImportTasks || canExport) && (canPaste || canExport) && (
            <div className="my-1 border-t border-boss-border" aria-hidden="true" />
          )}
          {/* Project group — whole-project JSON bundle import/export */}
          {(canPaste || canExport) && (
            <div data-testid="menu-group-project">
              <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-boss-text-muted">
                Projects
              </p>
              {canPaste && (
                <MenuItem
                  icon={ClipboardPaste}
                  label="Import Project"
                  hint="Add, merge, overwrite, or replace from an exported project"
                  testid="menu-paste-bundle"
                  onSelect={() => {
                    setOpen(false);
                    onPasteBundle?.();
                  }}
                />
              )}
              {canExport && (
                <MenuItem
                  icon={FileDown}
                  label="Export Project"
                  hint="Whole project as a portable JSON bundle"
                  testid="menu-export-project"
                  disabled={downloading}
                  onSelect={() =>
                    download(
                      `${API_URL}/api/projects/projects/${projectId}/export-bundle`,
                      `${safeName}_project.json`,
                      'Project bundle downloaded'
                    )
                  }
                />
              )}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
